import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { fmtScore, recommendationColor } from "../lib/format";
import { Loader2 } from "lucide-react";

const INDICATOR_LABELS = {
  cumulative_return: "Retorno acumulado",
  trend_slope: "Inclinação da tendência",
  moving_avg_deviation: "Desvio da média móvel",
  volume_trend: "Tendência de volume",
  rsi_14: "RSI (14)",
  macd_hist: "MACD histograma",
};

export default function ScoreBreakdownModal({ open, onClose, stock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !stock) return;
    setLoading(true);
    setData(null);
    apiClient
      .get(`/stocks/${stock.id}/recommendation`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, stock]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="score-breakdown-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-3">
            {stock?.ticker} — Transparência do Score
            {data && (
              <Badge className={`${recommendationColor(data.recommendation_label)} border`}>
                {data.recommendation_label}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Detalhamento das 5 janelas de análise (30 / 15 / 5 / 3 / 2 dias). Cada janela
            agrega indicadores técnicos normalizados em [-1, +1], amortecidos pela volatilidade.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando análise…
          </div>
        )}

        {data && (
          <div className="space-y-6 mt-2">
            <div className="rounded-md border bg-muted/40 p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Score Composto</div>
                  <div className="font-mono-tab text-2xl font-bold" data-testid="score-composite">
                    {fmtScore(data.composite_score)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Recomendação</div>
                  <div className="font-display text-base font-semibold">{data.recommendation_label}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">USD/BRL</div>
                  <div className="font-mono-tab text-base">
                    R$ {Number(data.usd_brl_reference_rate || 0).toFixed(4)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Data</div>
                  <div className="font-mono-tab text-base">{data.analysis_date}</div>
                </div>
              </div>
              <div className="mt-4 text-xs text-muted-foreground font-mono-tab leading-relaxed">
                Fórmula: Score = Σ ( peso(w) × WindowScore(w) ), com WindowScore(w) = 100 ×
                amortecedor_volatilidade × média_ponderada(indicadores).
              </div>
            </div>

            <div className="space-y-3">
              {data.windows.map((w) => (
                <div
                  key={w.window_days}
                  className="rounded-md border bg-card p-4"
                  data-testid={`window-${w.window_days}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono-tab">
                        {w.window_days}d
                      </Badge>
                      <span className="text-sm">
                        Peso <span className="font-mono-tab">{(w.weight * 100).toFixed(0)}%</span>
                      </span>
                      <span className="text-xs text-muted-foreground">
                        · Amortecedor vol.{" "}
                        <span className="font-mono-tab">
                          {(Number(w.volatility_damping || 0) * 100).toFixed(0)}%
                        </span>
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-tab text-lg font-semibold">{fmtScore(w.window_score)}</div>
                      <Badge
                        className={`${recommendationColor(w.window_label)} border text-[10px]`}
                        variant="outline"
                      >
                        {w.window_label}
                      </Badge>
                    </div>
                  </div>

                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-muted-foreground uppercase tracking-[0.15em] text-[10px]">
                        <tr>
                          <th className="text-left py-1">Indicador</th>
                          <th className="text-right py-1">Bruto</th>
                          <th className="text-right py-1">Normalizado</th>
                          <th className="text-right py-1">Peso</th>
                          <th className="text-right py-1">Contribuição</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono-tab">
                        {w.indicators.map((ind) => (
                          <tr key={ind.indicator_code} className="border-t border-border">
                            <td className="py-1.5 pr-2">
                              {INDICATOR_LABELS[ind.indicator_code] || ind.indicator_code}
                            </td>
                            <td className="text-right">{Number(ind.raw_value).toFixed(4)}</td>
                            <td className="text-right">
                              <span className={ind.normalized_value >= 0 ? "text-buy" : "text-sell"}>
                                {Number(ind.normalized_value).toFixed(3)}
                              </span>
                            </td>
                            <td className="text-right">{Number(ind.weight).toFixed(2)}</td>
                            <td className="text-right font-semibold">
                              {fmtScore(ind.contribution)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
